import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import customerApi from "../lib/customerApi";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";

export default function CustomerRegister() {
  const { lang } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    phone: "",
    address: "",
    gstin: "",
    login_id: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await customerApi.post("/customers/register", {
        ...form,
        gstin: form.gstin.toUpperCase().trim(),
        login_id: form.login_id.toLowerCase().trim(),
      });
      toast.success(
        lang === "en"
          ? "Registration submitted! We'll review and approve soon."
          : "నమోదు పూర్తయింది! త్వరలో అడ్మిన్ ఆమోదిస్తారు."
      );
      navigate("/customer/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="customer-register-page" className="bg-[#fbf8f1] min-h-[80vh] py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
          {lang === "en" ? "Wholesale / Regular Customer" : "హోల్‌సేల్ / రెగ్యులర్ కస్టమర్"}
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tighter font-bold text-stone-900 mt-1">
          {lang === "en" ? "Register your business" : "మీ వ్యాపారం నమోదు చేయండి"}
        </h1>
        <p className="text-stone-600 mt-2 text-sm">
          {lang === "en"
            ? "GSTIN-verified businesses get a personal portal with pre-booking, invoices and order history."
            : "GST నమోదైన వ్యాపారాలకు ప్రీ-బుకింగ్, ఇన్‌వాయిస్‌లు, ఆర్డర్ చరిత్రతో ప్రత్యేక పోర్టల్."}
        </p>

        <form onSubmit={submit} className="mt-8 bg-white border border-stone-200 rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <RegField name="business_name" label="Business / Shop name" value={form.business_name} onChange={onChange} required testId="reg-business" />
          <RegField name="contact_name" label="Contact person" value={form.contact_name} onChange={onChange} required testId="reg-contact" />
          <RegField name="phone" label="Mobile" value={form.phone} onChange={onChange} required testId="reg-phone" />
          <RegField name="gstin" label="GSTIN (15 chars)" value={form.gstin} onChange={onChange} required testId="reg-gstin" />
          <div className="sm:col-span-2">
            <RegField name="address" label="Full address" value={form.address} onChange={onChange} required testId="reg-address" />
          </div>
          <RegField name="login_id" label="Choose login ID (lowercase)" value={form.login_id} onChange={onChange} required testId="reg-loginid" />
          <RegField name="password" label="Password (min 6)" value={form.password} onChange={onChange} required type="password" testId="reg-password" />

          <div className="sm:col-span-2 flex items-center justify-between mt-2">
            <Link to="/customer/login" className="text-sm text-[#274e13] hover:underline">
              {lang === "en" ? "Already have an account? Sign in" : "ఇప్పటికే ఖాతా ఉందా? లాగిన్"}
            </Link>
            <button
              type="submit"
              data-testid="reg-submit"
              disabled={loading}
              className="bg-[#274e13] hover:bg-[#38761d] text-white px-7 py-3 rounded-sm font-semibold text-sm tracking-wide transition-colors disabled:opacity-60"
            >
              {loading ? "Submitting..." : (lang === "en" ? "Submit for approval" : "ఆమోదం కోసం పంపండి")}
            </button>
          </div>
        </form>

        <div className="mt-6 text-xs text-stone-500 leading-relaxed">
          {lang === "en"
            ? "After you submit, our team reviews your GSTIN and approves the account (usually within a few hours). You'll be able to login once approved."
            : "మీ నమోదు తరువాత, మా బృందం GST వివరాలు సరిచూసి కొన్ని గంటల్లో ఆమోదిస్తుంది. తరువాత మీరు లాగిన్ అవ్వగలరు."}
        </div>
      </div>
    </div>
  );
}

function RegField({ label, testId, ...rest }) {
  return (
    <div>
      <label className="text-[10px] tracking-wider uppercase text-stone-500 font-semibold">{label}</label>
      <input
        data-testid={testId}
        {...rest}
        className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#274e13]"
      />
    </div>
  );
}
